import { useState, useEffect } from "react";
import { api } from "../App";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";
import { 
  Building2, MapPin, Phone, Mail, Globe, 
  Facebook, Instagram, Twitter, Linkedin, Youtube,
  Users, Target, Eye, History, Loader2, Image as ImageIcon
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_social-circle-37/artifacts/vzzvahrb_IMG-20251227-WA0000.jpg";

const AboutPage = () => {
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const response = await api.get("/about");
      setAbout(response.data);
    } catch (_error) {
      toast.error("Failed to load about page");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const socialLinks = [
    { url: about?.facebook_url, icon: Facebook, label: "Facebook", color: "text-blue-600" },
    { url: about?.instagram_url, icon: Instagram, label: "Instagram", color: "text-pink-600" },
    { url: about?.twitter_url, icon: Twitter, label: "Twitter", color: "text-sky-500" },
    { url: about?.linkedin_url, icon: Linkedin, label: "LinkedIn", color: "text-blue-700" },
    { url: about?.youtube_url, icon: Youtube, label: "YouTube", color: "text-red-600" },
    { url: about?.website_url, icon: Globe, label: "Website", color: "text-teal-600" },
  ].filter(link => link.url);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto py-6 px-4" data-testid="about-page">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="Logo" 
            className="w-24 h-24 mx-auto rounded-full object-cover shadow-lg mb-4"
          />
          <h1 className="text-3xl font-bold text-primary">
            {about?.organization_name || "LUNAWAT FOUNDATION"}
          </h1>
          {about?.description && (
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              {about.description}
            </p>
          )}
        </div>

        {/* Mission & Vision */}
        {(about?.mission || about?.vision) && (
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            {about?.mission && (
              <Card className="bg-gradient-to-br from-teal-50 to-teal-100/50 dark:from-teal-950/30 dark:to-teal-900/20 border-teal-200">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-teal-700 dark:text-teal-400">
                    <Target className="w-5 h-5" />
                    Our Mission
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-teal-900 dark:text-teal-100">{about.mission}</p>
                </CardContent>
              </Card>
            )}
            {about?.vision && (
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/30 dark:to-amber-900/20 border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg text-amber-700 dark:text-amber-400">
                    <Eye className="w-5 h-5" />
                    Our Vision
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-amber-900 dark:text-amber-100">{about.vision}</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* History */}
        {about?.history && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5" />
                Our History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-line">{about.history}</p>
            </CardContent>
          </Card>
        )}

        {/* Team/Committee */}
        {about?.team_members && about.team_members.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Our Team
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {about.team_members.map((member, index) => (
                  <div key={index} className="text-center">
                    <Avatar className="w-20 h-20 mx-auto mb-2">
                      <AvatarImage src={member.photo} />
                      <AvatarFallback className="bg-primary/10 text-primary text-lg font-semibold">
                        {member.name?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <h4 className="font-semibold text-sm">{member.name}</h4>
                    {member.designation && (
                      <p className="text-xs text-muted-foreground">{member.designation}</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gallery */}
        {about?.gallery_images && about.gallery_images.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ImageIcon className="w-5 h-5" />
                Gallery
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {about.gallery_images.map((img, index) => (
                  <div 
                    key={index} 
                    className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => setSelectedImage(img)}
                  >
                    <img 
                      src={img} 
                      alt={`Gallery ${index + 1}`} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Information */}
        {(about?.contact_address || about?.contact_phone || about?.contact_email) && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {about?.contact_address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Address</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">{about.contact_address}</p>
                  </div>
                </div>
              )}
              {about?.contact_phone && (
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Phone</p>
                    <a 
                      href={`tel:${about.contact_phone}`} 
                      className="text-sm text-primary hover:underline"
                    >
                      {about.contact_phone}
                    </a>
                  </div>
                </div>
              )}
              {about?.contact_email && (
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Email</p>
                    <a 
                      href={`mailto:${about.contact_email}`} 
                      className="text-sm text-primary hover:underline"
                    >
                      {about.contact_email}
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Social Media Links */}
        {socialLinks.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Connect With Us
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border hover:bg-muted transition-colors ${link.color}`}
                  >
                    <link.icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{link.label}</span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!about?.description && !about?.mission && !about?.vision && !about?.history && 
         (!about?.team_members || about.team_members.length === 0) && 
         (!about?.gallery_images || about.gallery_images.length === 0) &&
         !about?.contact_address && !about?.contact_phone && !about?.contact_email &&
         socialLinks.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Building2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg mb-2">About page coming soon!</p>
              <p className="text-sm">Check back later for more information about LUNAWAT FOUNDATION.</p>
            </CardContent>
          </Card>
        )}

        {/* Image Lightbox */}
        {selectedImage && (
          <div 
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <img 
              src={selectedImage} 
              alt="Gallery" 
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AboutPage;
